import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { ClearButton, ClearInput } from "@components";
import { Grid } from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import SettingsCheckboxList from "./components/SettingsCheckboxList";
import classnames from "classnames";

export const PercentSettingContents = ({
  editedWorkTypeName,
  usedWorkTypes,
  usedSections,
  onSelectWorkType,
  onSelectSection,
  percent,
  percentPlaceholder,
  onPercentChange,
  availableWorkTypes,
  availableSections,
  blacklistedWorkTypes,
  hideWorkTypeList,
  hideSectionList,
  maxWorkTypesVisible = 3,
  maxSectionsVisible = 3,
  minValue=-100,
  maxValue=999,
  allowNegative,
  showOnlyUsedWorkTypes,
  showPercentageWorkTypesTooltip,
  allowSelectAllWorkTypes,
  allowSelectAllSections,
  useSectionsOf,
  showSectionsSettings = true,
  onAdvancedSettingsToggle,
  className,
  ...percentInputProps
}) => {
  const { t } = useTranslation();
  
  return (
    <Grid
      item container
      wrap="nowrap"
      className={classnames("mt-2 gap-2", className)}
    >
      <Grid
        item container
        direction="column"
        className="w-max"
      >
        <p className="mb-2">
          {t("common.percentage")}
        </p>
        <Grid
          item container
          alignItems="center"
          className="w-96"
          wrap="nowrap"
        >
          <ClearInput
            confirmOnEnter={false}
            name="percent of"
            value={percent}
            placeholder={percentPlaceholder}
            max={maxValue}
            min={minValue}
            allowNegative={allowNegative}
            onChange={onPercentChange}
            addInitialBorder
            decimals={2}
            showDecimals
            fullWidth
            {...percentInputProps}
          />
        </Grid>
      </Grid>
      <Grid
        item
        container
        justifyContent="center"
        alignItems="center"
        className="h-max w-64 mt-8 transparent-2 text-md"
      >
        {`% ${t("common.of")}`}
      </Grid>
      {
        Boolean(usedWorkTypes && onSelectWorkType) &&
        <SettingsCheckboxList
          availableItems={availableWorkTypes}
          selectedIds={usedWorkTypes}
          onSelectItems={onSelectWorkType}
          hideList={hideWorkTypeList}
          maxVisible={maxWorkTypesVisible}
          showOnlySelected={showOnlyUsedWorkTypes}
          blacklistedItems={blacklistedWorkTypes}
          editedItemName={editedWorkTypeName}
          showPercentageTooltip={showPercentageWorkTypesTooltip}
          title={t("common.work_types")}
          emptyLabel={t("views.editor.dialogs.automation.empty")}
          allowSelectAll={allowSelectAllWorkTypes}
          headerAction={onAdvancedSettingsToggle && (
            <ClearButton
              size="small"
              className="w-max text-sm"
              endIcon={<ExpandMore className={showSectionsSettings ? "rotate-90" : "-rotate-90"} />}
              onClick={onAdvancedSettingsToggle}
            >
              {t("common.advanced_settings")}
            </ClearButton>
          )}
        />
      }
      {
        Boolean(showSectionsSettings && useSectionsOf && usedSections && onSelectSection) &&
        <Grid
          item
          container
          justifyContent="center"
          alignItems="center"
          className="h-max w-56 mt-8 transparent-2 text-md"
        >
          {t("common.of")}
        </Grid>
      }
      {
        Boolean(showSectionsSettings && usedSections && onSelectSection) &&
        <SettingsCheckboxList
          availableItems={availableSections}
          selectedIds={usedSections}
          onSelectItems={onSelectSection}
          hideList={hideSectionList}
          maxVisible={maxSectionsVisible}
          title={useSectionsOf
            ? ""
            : t(
              usedWorkTypes && onSelectWorkType
                ? "common.apply_to"
                : "proposal_parts.section_titles.section"
            )
        }
          emptyLabel={t("views.editor.dialogs.automation.empty_sections")}
          selectAllTitle={t("common.all_sections")}
          showSelectAllOnList
          allowSelectAll={allowSelectAllSections}
        />
      }
    </Grid>
  );
};

PercentSettingContents.propTypes = {
  usedWorkTypes: arrayOf(number).isRequired,
  usedSections: arrayOf(number),
  onSelectWorkType: func.isRequired,
  onSelectSection: func,
  percent: number.isRequired,
  percentPlaceholder: string,
  onPercentChange: func.isRequired,
  editedWorkTypeName: string,
  availableWorkTypes: arrayOf(shape({
    id: number.isRequired,
    name: string.isRequired,
    percentageWorkTypes: arrayOf(shape({
      id: number.isRequired,
      name: string,
    }))
  })),
  availableSections: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
  })),
  blacklistedWorkTypes: arrayOf(shape({
    id: number.isRequired,
    sections: arrayOf(string).isRequired
  })),
  clickDialogMessageContent: string,
  clickDialogMessageTitle: string,
  allowNegative: bool,
  hideWorkTypeList: bool,
  hideSectionList: bool,
  maxWorkTypesVisible: number,
  maxSectionsVisible: number,
  showOnlyUsedWorkTypes: bool,
  showPercentageWorkTypesTooltip: bool,
  className: string,
  minValue: number,
  maxValue: number,
  allowSelectAllWorkTypes: bool,
  allowSelectAllSections: bool,
  useSectionsOf: bool,
  showSectionsSettings: bool,
  onAdvancedSettingsToggle: func,
};
